import { queryClient as defaultQueryClient } from '@/plugins/vueQuery';
import type { QueryInvalidationParams } from '@/types/query';
import { queryKeys } from '@/utils/constants';
import { getAgreementAthletesQueryKey } from '@/utils/queries/agreements';

const MODULE_KEY = 'athletes';

/** Query Key Getters */

/**
 * Cache key for the team athletes list (`getTeamAthletesList` API).
 *
 * The list query is keyed with several volatile trailing params (page size,
 * cursor, search, academic year, trashed, sort). This getter owns the stable
 * prefix `['athletes', 'getTeamAthletesList', <teamTwrn>]`; read sites append
 * their pagination/sort params after it. Invalidations only ever need the
 * prefix, so pass fewer args to invalidate a broader slice of the cache.
 */
export const getTeamAthletesListQueryKey = (teamTwrn?: string | null) => {
  const key = [MODULE_KEY, queryKeys.TEAM_ATHLETES_LIST];

  if (teamTwrn) {
    key.push(teamTwrn);
  }

  return key;
};

/**
 * Cache key for a single athlete's team profile (`getTeamAthleteProfile` API).
 * Fully encodable: `['athletes', 'getTeamAthleteProfile', <teamTwrn>, <athleteTwrn>]`.
 */
export const getTeamAthleteProfileQueryKey = (
  teamTwrn?: string | null,
  athleteTwrn?: string | null,
) => {
  const key = [MODULE_KEY, queryKeys.TEAM_ATHLETE_PROFILE];

  if (teamTwrn) {
    key.push(teamTwrn);
  }

  if (athleteTwrn) {
    if (!teamTwrn) {
      console.warn(
        '[getTeamAthleteProfileQueryKey] athleteTwrn was provided without teamTwrn. teamTwrn is required when athleteTwrn is passed.',
        {
          teamTwrn,
          athleteTwrn,
        },
      );
      return key;
    }
    key.push(athleteTwrn);
  }

  return key;
};

/**
 * Cache key for the eligible athletes list (`getEligibleAthletes` API).
 *
 * Eligible athletes are read with several different trailing shapes across the
 * app (a filter params object, a budget twrn, or a multi-budget marker). This
 * getter owns only the stable prefix
 * `['athletes', 'getEligibleAthletes', <teamTwrn>, <budgetTwrn>]`; read sites
 * append their filter object / mode after it. That keeps a single reliable
 * prefix for invalidation while leaving each read's exact key intact.
 */
export const getEligibleAthletesQueryKey = (
  teamTwrn?: string | null,
  budgetTwrn?: string | null,
) => {
  const key = [MODULE_KEY, queryKeys.ELIGIBLE_ATHLETES];

  if (teamTwrn) {
    key.push(teamTwrn);
  }

  if (budgetTwrn) {
    if (!teamTwrn) {
      console.warn(
        '[getEligibleAthletesQueryKey] budgetTwrn was provided without teamTwrn. teamTwrn is required when budgetTwrn is passed.',
        {
          teamTwrn,
          budgetTwrn,
        },
      );
      return key;
    }
    key.push(budgetTwrn);
  }

  return key;
};

/**
 * Cache key for an athlete's documents (`getAthleteDocuments` API).
 * Fully encodable: `['athletes', 'getAthleteDocuments', <teamTwrn>, <athleteTwrn>]`.
 *
 * Canonical order is team-scoped (teamTwrn before athleteTwrn), matching other
 * getters in this module. Call sites that previously used athlete-first order
 * must migrate to this shape.
 */
export const getAthleteDocumentsQueryKey = (
  teamTwrn?: string | null,
  athleteTwrn?: string | null,
) => {
  const key = [MODULE_KEY, queryKeys.ATHLETE_DOCUMENTS];

  if (teamTwrn) {
    key.push(teamTwrn);
  }

  if (athleteTwrn) {
    if (!teamTwrn) {
      console.warn(
        '[getAthleteDocumentsQueryKey] athleteTwrn was provided without teamTwrn. teamTwrn is required when athleteTwrn is passed.',
        {
          teamTwrn,
          athleteTwrn,
        },
      );
      return key;
    }
    key.push(athleteTwrn);
  }

  return key;
};

/**
 * Cache key for athlete earnings (`getAthleteEarnings` API).
 * Fully encodable: `['athletes', 'getAthleteEarnings', <teamTwrn>, <athleteTwrn>, <budgetTwrn>]`.
 */
export const getAthleteEarningsQueryKey = (
  teamTwrn?: string | null,
  athleteTwrn?: string | null,
  budgetTwrn?: string | null,
) => {
  const key: (string | null)[] = [MODULE_KEY, queryKeys.ATHLETE_EARNINGS];

  if (teamTwrn) {
    key.push(teamTwrn);
  }

  if (athleteTwrn) {
    if (!teamTwrn) {
      console.warn(
        '[getAthleteEarningsQueryKey] athleteTwrn was provided without teamTwrn. teamTwrn is required when athleteTwrn is passed.',
        {
          teamTwrn,
          athleteTwrn,
        },
      );
      return key;
    }
    key.push(athleteTwrn);
  }

  if (budgetTwrn) {
    if (!athleteTwrn) {
      console.warn(
        '[getAthleteEarningsQueryKey] budgetTwrn was provided without athleteTwrn. athleteTwrn is required when budgetTwrn is passed.',
        {
          athleteTwrn,
          budgetTwrn,
        },
      );
      return key;
    }
    key.push(budgetTwrn);
  }

  return key;
};

/**
 * Cache key for athlete notes (`getAthleteNotes` API).
 * Fully encodable: `['athletes', 'getAthleteNotes', <teamTwrn>, <athleteTwrn>]`.
 */
export const getAthleteNotesQueryKey = (
  teamTwrn?: string | null,
  athleteTwrn?: string | null,
) => {
  const key: (string | null)[] = [MODULE_KEY, queryKeys.ATHLETE_NOTES];

  if (teamTwrn) {
    key.push(teamTwrn);
  }

  if (athleteTwrn) {
    if (!teamTwrn) {
      console.warn(
        '[getAthleteNotesQueryKey] athleteTwrn was provided without teamTwrn. teamTwrn is required when athleteTwrn is passed.',
        {
          teamTwrn,
          athleteTwrn,
        },
      );
      return key;
    }
    key.push(athleteTwrn);
  }

  return key;
};

/**
 * Cache key for athlete agreements (`getAthleteAgreements` API).
 * Fully encodable:
 * `['athletes', 'getAthleteAgreements', <teamTwrn>, <athleteTwrn>, <academicYearId>, <limit>]`.
 */
export const getAthleteAgreementsQueryKey = (
  teamTwrn?: string | null,
  athleteTwrn?: string | null,
  academicYearId?: string | null,
  limit?: number | null,
) => {
  const key: (string | number | null)[] = [MODULE_KEY, queryKeys.ATHLETE_AGREEMENTS];

  if (teamTwrn) {
    key.push(teamTwrn);
  }

  if (athleteTwrn) {
    if (!teamTwrn) {
      console.warn(
        '[getAthleteAgreementsQueryKey] athleteTwrn was provided without teamTwrn. teamTwrn is required when athleteTwrn is passed.',
        {
          teamTwrn,
          athleteTwrn,
        },
      );
      return key;
    }
    key.push(athleteTwrn);
  }

  if (academicYearId) {
    if (!athleteTwrn) {
      console.warn(
        '[getAthleteAgreementsQueryKey] academicYearId was provided without athleteTwrn. athleteTwrn is required when academicYearId is passed.',
        {
          athleteTwrn,
          academicYearId,
        },
      );
      return key;
    }
    key.push(academicYearId);
  }

  if (limit !== undefined && limit !== null) {
    if (!academicYearId) {
      console.warn(
        '[getAthleteAgreementsQueryKey] limit was provided without academicYearId. academicYearId is required when limit is passed.',
        {
          academicYearId,
          limit,
        },
      );
      return key;
    }
    key.push(limit);
  }

  return key;
};

/**
 * Cache key for a C&R athlete profile (`useCRAthleteProfile` / `getRecruitDetails`).
 * Fully encodable: `['athletes', 'getCRAthleteProfile', <contactId>]`.
 */
export const getCRAthleteProfileQueryKey = (contactId?: number | null) => {
  const key: (string | number | null)[] = [MODULE_KEY, queryKeys.CR_ATHLETE_PROFILE];

  if (contactId !== undefined && contactId !== null) {
    key.push(contactId);
  }

  return key;
};

/**
 * Cache key for third-party NIL influencer overview (`ThirdPartyNilEarnings`).
 * Fully encodable: `['athletes', 'getThirdPartyNilInfluencer', <athleteAxleTwrn>]`.
 */
export const getThirdPartyNilInfluencerQueryKey = (athleteAxleTwrn?: string | null) => {
  const key: (string | null)[] = [MODULE_KEY, queryKeys.THIRD_PARTY_NIL_INFLCR];

  if (athleteAxleTwrn) {
    key.push(athleteAxleTwrn);
  }

  return key;
};

/**
 * Cache key for linkable profiles (`getLinkableProfiles` API).
 * Fully encodable: `['athletes', 'getLinkableProfiles', <teamTwrn>, <search>, <currentCursor>]`.
 *
 * Search comes before cursor so `[team, search]` prefix-invalidates all pages
 * for that search. When cursor is passed without search, a `null` sentinel is
 * inserted so cursor-only keys cannot collide with search-bearing keys.
 */
export const getLinkableProfilesQueryKey = (
  teamTwrn?: string | null,
  search?: string | null,
  currentCursor?: string | null,
) => {
  const key: (string | null)[] = [MODULE_KEY, queryKeys.LINKABLE_PROFILES];

  if (teamTwrn) {
    key.push(teamTwrn);
  }

  if (search !== undefined && search !== null) {
    if (!teamTwrn) {
      console.warn(
        '[getLinkableProfilesQueryKey] search was provided without teamTwrn. teamTwrn is required when search is passed.',
        {
          teamTwrn,
          search,
        },
      );
      return key;
    }
    key.push(search);
  }

  if (currentCursor !== undefined) {
    if (!teamTwrn) {
      console.warn(
        '[getLinkableProfilesQueryKey] currentCursor was provided without teamTwrn. teamTwrn is required when currentCursor is passed.',
        {
          teamTwrn,
          currentCursor,
        },
      );
      return key;
    }
    if (search === undefined || search === null) {
      key.push(null);
    }
    key.push(currentCursor);
  }

  return key;
};

/** Invalidation Helpers */

export const invalidateAllAthletes = (options?: QueryInvalidationParams) => {
  const client = options?.queryClient ?? defaultQueryClient;

  client.invalidateQueries({ queryKey: getTeamAthletesListQueryKey() });
  client.invalidateQueries({ queryKey: getTeamAthleteProfileQueryKey() });
  client.invalidateQueries({ queryKey: getEligibleAthletesQueryKey() });
  client.invalidateQueries({ queryKey: getAgreementAthletesQueryKey() });
};
